import * as React from "react";
import {faSearch}           from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {css, cx}            from "emotion";
import {rgba}               from "polished";

import "./search-input.scss";

import {bindInput}                                   from "@Common/react/bind-input";
import {ComponentLink, Link, makeChain, valueHelper} from "@Common/react/cst2";
import {Static2}                                     from "@Common/react/static-2";
import {UseEffect}                                   from "@Common/react/use-effect";
import {UseState}                                    from "@Common/react/use-state";
import {isBlankStr}                                  from "@Common/utils/strings";

import {consumeContext} from "@Xpresso/loaders/consume-context";

import {usePopoverAutoClose} from "../../../../../omni-search/suggestions-dialog/popover";
import {SuggestionsDialog}   from "../../../../../omni-search/suggestions-dialog/suggestions-dialog";

export const SearchInput = ({disableSuggestionsDialog=false, open, navBlock}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then(...consumeContext("company"))
    .then(...consumeContext("routerHistory"))
    .then(...consumeContext("themeScheme"))
    .then(...consumeContext("session"))
    .then(...consumeContext("userLevels"))

    .then("isOmniSearchEnabled", valueHelper(({company}) => company?.featureFlags?.omniSearch?.value ?? false))
    .then("onSearch", valueHelper(({apis, company, isOmniSearchEnabled, routerHistory, session, userLevels}) => (
        (text: string) =>
        {
            apis.next.omniSearch.addSearchHistoryRecord({record: {
                action: "search",
                search_handler: "suggestions",
                search_term: text,
                date_created: (new Date).toISOString(),

                suggestion_type: "search",
                suggestion_name: "",
                suggestion_data: text,

                id_company: company.idCompany,
                id_user: userLevels.idUser,
                id_branch: session.value.branchId,
                id_agent: session.value.agentId,
            }});
            routerHistory.push(
                (window.location.pathname.startsWith("/app") ? "" : "/app")
                + "/"
                + (isOmniSearchEnabled ? "explore" : "search")
                + `?q=${encodeURIComponent(text)}`
            );
        }
    )))

    .then("search", (_, next:ComponentLink<typeof UseState<string>>) => UseState({next, initValue: ""}))
    .then("ref", (_, next:ComponentLink<typeof Static2<HTMLInputElement>>) => Static2({next}))

    .then("suggestionsOpenState", (_, next:ComponentLink<typeof UseState<boolean>>) => (
        UseState({next, initValue: false})
    ))
    .then("suggestionsDialogRef", (_, next: ComponentLink<typeof Static2<HTMLDivElement>>) => Static2({next}))
    .then(({ref, suggestionsDialogRef, suggestionsOpenState}, next: Link<JSX.Element>) => (
        disableSuggestionsDialog
        ? next()
        : (
            <AutoClosePopover
                next={next}
                setOpen={suggestionsOpenState.onChange}
                popoverDiv={suggestionsDialogRef.get()}
                elementsToIgnore={[ref.get()]}
            />
        )
    ))
    .then(({ref, suggestionsOpenState}, next: Link<JSX.Element>) => (
        <UseEffect next={next} fn={() =>
        {
            if (!open.value && suggestionsOpenState.value) {
                suggestionsOpenState.onChange(false);
            } else if (open.value && document.activeElement === ref.get() && !suggestionsOpenState.value) {
                suggestionsOpenState.onChange(true);
            }
        }}/>
    ))

    .then(({
        apis,
        company,
        isOmniSearchEnabled,
        onSearch,
        ref,
        search,
        session,
        suggestionsOpenState,
        suggestionsDialogRef,
        themeScheme,
        userLevels,
    }) => (
        <div
            className={cx(
                "search-input search-input-79y8", {open: open.value},
            )}
            style={{
                color: themeScheme.xpresso.menuTextColor,
                background: themeScheme.xpresso.menuBackgroundColor,
            }}
            onClick={async (e) => {
                e.preventDefault();
                if (await navBlock?.block() === true) {
                    return;
                }

                if (!open.value) {
                    open.onChange(true);
                    ref.get().focus();
                }
            }}
        >
            {isOmniSearchEnabled && !disableSuggestionsDialog && (
                <SuggestionsDialog
                    ref={suggestionsDialogRef.set}
                    anchor="top-right"
                    getSearchSuggestions={apis.next.omniSearch.getSearchSuggestions}
                    origin={{anchor: "bottom-right", rect: ref.get()?.getBoundingClientRect(), offset: {y: 5}}}
                    open={suggestionsOpenState.value}
                    recordActivity={activity => apis.next.omniSearch.addSearchHistoryRecord({record: {
                        action: "click",
                        search_handler: "suggestions",
                        search_term: activity.query,
                        date_created: (new Date).toISOString(),

                        suggestion_type: activity.type,
                        suggestion_name: activity.type === "template" ? activity.data.name : "",
                        suggestion_data: activity.type === "template" ? activity.data.id.toString() : activity.data.q,

                        id_company: company.idCompany,
                        id_user: userLevels.idUser,
                        id_branch: session.value.branchId,
                        id_agent: session.value.agentId,
                    }})}
                    searchQuery={search.value}
                />
            )}
            <input
                ref={ref.set}
                className={css`
                    color: ${themeScheme.xpresso.menuTextColor};
                    &::placeholder {
                        color: ${rgba(themeScheme.xpresso.menuTextColor, 0.7)};
                    }
                `}
                placeholder={"Search"}
                {...bindInput(search)}
                onBlur={(e) => {
                    if (open.value && !suggestionsDialogRef.get()?.contains(e.relatedTarget as HTMLElement)) {
                        open.onChange(false);
                        suggestionsOpenState.onChange(false);
                    }
                }}
                onFocus={() => suggestionsOpenState.onChange(true)}
                onKeyDown={(e) => {
                    if (e.key === "Enter" && !isBlankStr(search.value)) {
                        search.onChange(null);
                        onSearch(search.value);
                    }
                }}
                xd-qa-type="text_field"
                xd-qa-name="search"
            />
            <FontAwesomeIcon icon={faSearch}/>
        </div>
    ))
    .run()
);

const AutoClosePopover = ({elementsToIgnore, next, popoverDiv, setOpen}:{
    setOpen: (open: boolean) => void,
    popoverDiv: HTMLDivElement,
    elementsToIgnore: HTMLElement[],
    next: Link<JSX.Element>,
}) => next(usePopoverAutoClose(setOpen, popoverDiv, elementsToIgnore));

